/**
 * Candidate-video upload client.
 *
 * Uses S3 multipart upload for files large enough to make a retry of the
 * whole blob expensive — a 30-second video on flaky wifi otherwise means the
 * candidate re-records from scratch. Smaller files keep the simple presigned
 * PUT path so recorders that can never produce >6MB stay on the fast path.
 */

import { AXIOS_INSTANCE } from '../../services/api-client';

export type UploadProgress = {
  uploaded: number;
  total: number;
  partsCompleted: number;
  partsTotal: number;
  multipart: boolean;
};

export type UploadResult = {
  storageUri: string;
  contentType: string;
  fileSize: number;
};

const MULTIPART_THRESHOLD_BYTES = 6 * 1024 * 1024; // 6 MiB
const MULTIPART_PART_SIZE = 5 * 1024 * 1024; // 5 MiB — S3 minimum

type SinglePartContext = {
  upload_url: string;
  storage_uri: string;
  headers?: Record<string, string>;
  bucket: string;
  object_key: string;
  expires_in_seconds: number;
};

type MultipartInitContext = {
  bucket: string;
  object_key: string;
  storage_uri: string;
  upload_id: string;
  max_parts: number;
  min_part_size_bytes: number;
};

type MultipartPartUrl = {
  bucket: string;
  object_key: string;
  upload_id: string;
  part_number: number;
  upload_url: string;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function putBlobWithRetry(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  attempt = 0,
): Promise<Response> {
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body,
    });
    if (!response.ok) {
      // 5xx are worth retrying; 4xx aren't.
      if (response.status >= 500 && attempt < 4) {
        await sleep(500 * 2 ** attempt);
        return putBlobWithRetry(url, body, headers, attempt + 1);
      }
      throw new Error(`Upload failed (${response.status})`);
    }
    return response;
  } catch (err) {
    if (attempt < 4) {
      await sleep(500 * 2 ** attempt);
      return putBlobWithRetry(url, body, headers, attempt + 1);
    }
    throw err;
  }
}

async function singlePartUpload(
  recorderToken: string,
  filename: string,
  blob: Blob,
  onProgress: (progress: UploadProgress) => void,
): Promise<UploadResult> {
  const init = await AXIOS_INSTANCE.post<SinglePartContext>(
    `/r/${recorderToken}/upload-url`,
    {
      filename,
      content_type: blob.type || 'video/webm',
      file_size_bytes: blob.size,
    },
  );
  const headers = init.data.headers || { 'Content-Type': blob.type || 'video/webm' };
  await putBlobWithRetry(init.data.upload_url, blob, headers);
  onProgress({
    uploaded: blob.size,
    total: blob.size,
    partsCompleted: 1,
    partsTotal: 1,
    multipart: false,
  });
  return {
    storageUri: init.data.storage_uri,
    contentType: blob.type || 'video/webm',
    fileSize: blob.size,
  };
}

async function multipartUpload(
  recorderToken: string,
  filename: string,
  blob: Blob,
  onProgress: (progress: UploadProgress) => void,
): Promise<UploadResult> {
  const init = await AXIOS_INSTANCE.post<MultipartInitContext>(
    `/r/${recorderToken}/multipart/init`,
    {
      filename,
      content_type: blob.type || 'video/webm',
      file_size_bytes: blob.size,
    },
  );

  const partSize = Math.max(
    init.data.min_part_size_bytes || MULTIPART_PART_SIZE,
    MULTIPART_PART_SIZE,
  );
  const totalParts = Math.max(1, Math.ceil(blob.size / partSize));
  if (totalParts > init.data.max_parts) {
    throw new Error('Recording is too large for the configured upload settings.');
  }

  const completedParts: { part_number: number; etag: string }[] = [];
  let uploadedBytes = 0;
  try {
    for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(blob.size, start + partSize);
      const chunk = blob.slice(start, end);
      const partUrl = await AXIOS_INSTANCE.post<MultipartPartUrl>(
        `/r/${recorderToken}/multipart/part-url`,
        {
          upload_id: init.data.upload_id,
          part_number: partNumber,
        },
      );
      const response = await putBlobWithRetry(
        partUrl.data.upload_url,
        chunk,
        { 'Content-Type': blob.type || 'video/webm' },
      );
      const etag = (response.headers.get('etag') || '').replace(/"/g, '');
      if (!etag) {
        throw new Error('Upload server did not return an ETag for the multipart part.');
      }
      completedParts.push({ part_number: partNumber, etag });
      uploadedBytes += chunk.size;
      onProgress({
        uploaded: uploadedBytes,
        total: blob.size,
        partsCompleted: partNumber,
        partsTotal: totalParts,
        multipart: true,
      });
    }
    await AXIOS_INSTANCE.post(`/r/${recorderToken}/multipart/complete`, {
      upload_id: init.data.upload_id,
      parts: completedParts,
    });
    return {
      storageUri: init.data.storage_uri,
      contentType: blob.type || 'video/webm',
      fileSize: blob.size,
    };
  } catch (err) {
    // Best-effort cleanup so abandoned multipart sessions don't accrue S3 charges.
    try {
      await AXIOS_INSTANCE.post(`/r/${recorderToken}/multipart/abort`, {
        upload_id: init.data.upload_id,
      });
    } catch (_) {
      /* swallow — primary error is more interesting */
    }
    throw err;
  }
}

export async function uploadRecordingForToken(
  recorderToken: string,
  filename: string,
  blob: Blob,
  onProgress: (progress: UploadProgress) => void,
): Promise<UploadResult> {
  if (blob.size <= MULTIPART_THRESHOLD_BYTES) {
    return singlePartUpload(recorderToken, filename, blob, onProgress);
  }
  return multipartUpload(recorderToken, filename, blob, onProgress);
}
